import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  Dropdown,
  Input,
  message,
  Modal,
  notification,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Typography
} from "antd";
import type { MenuProps } from "antd";
import {
  ArrowsAltOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  DownOutlined,
  FormOutlined,
  LinkOutlined,
  SearchOutlined,
  ShrinkOutlined,
  UserOutlined
} from "@ant-design/icons";
import { useAgGridTreeExpandToggle } from "hooks/use-ag-grid-tree-expand-toggle";
import type { AgGridReact } from "ag-grid-react";
import type { FilterChangedEvent, GridApi } from "ag-grid-enterprise";
import { updateGridSelectionOnFilter } from "utils/utils";
import BlankState from "components/blank-state";
import BulkSelectionActionBar from "components/bulk-selection-action-bar";
import ErrorBoundary from "components/error-boundary";
import ColumnConfigViews, {
  type TSaveColumnState
} from "components/column-config-views";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import type { ConditionalFormattingRule } from "components/conditional-formatting";
import CiqIconBox from "components/svg-icons/ciq-icon-box";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import { ProjectContext } from "context/ProjectProvider";
import { EFeaturePageId } from "constants/index";
import { exportEntityLogAsCSV } from "entity-app/utils/export-entity-log-as-csv";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import {
  exportEntityLogAsExcel,
  sanitizeSheetName
} from "components/excel-ag-grid-export/export-entity-log-as-excel";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";
import ExportOptionsPopover, {
  ExportFormatOption,
  ExportSelection
} from "components/export-options-popover";
import { DateUtils } from "utils/dateutils";
import PrintViewValidationModal from "pages/print-view-layout/Print-View-Validation-Modal";
import {
  PRINT_VIEW_STORAGE_KEY,
  type DesignScopeLogPrintViewState
} from "pages/print-view-layout/model";
import {
  designScopeExportEventType,
  designScopePdfFileBaseName
} from "./shared/design-scope-export-audit";
import {
  FilterChipComponent,
  type FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import {
  clearPendingTemplateSeed,
  dsmTemplatesApi,
  getPendingTemplateSeed,
  foldToPair,
  type Classification,
  type Classifications,
  type CsiSystem,
  type DsmScopeItem,
  type ScopeItemInput
} from "design-scope";
import type { DesignScopeListItem } from "mock-data/design-scope";
import {
  createDesignScopeInstances,
  deleteDesignScopeInstances,
  updateDesignScopeInstances,
  DESIGN_SCOPE_FEATURE_TYPE_ID
} from "services/design-scope-apis";
import { useDesignScopeList } from "./hooks/use-design-scope-list";
import { useDesignScopeColumnConfig } from "./hooks/use-design-scope-column-config";
import {
  DEFAULT_DESIGN_SCOPE_VIEW_LAYOUT,
  type DesignScopePageView,
  type DesignScopeViewLayout,
  readDesignScopeViewLayout,
  withDesignScopeViewMeta
} from "./shared/design-scope-view-meta";
import { reconcileDesignScopeColumnList } from "./shared/design-scope-column-order";
import { ensureDesignScopeDefaultSort } from "./shared/design-scope-default-sort";
import { useProjectEntities } from "./hooks/use-project-entities";
import { useLevelOfDetail } from "./hooks/use-level-of-detail";
import { useDesignScopeResponsibilities } from "./hooks/use-design-scope-responsibilities";
import ScopeTable from "./components/scope-table";
import ScopeDetailPanel from "./components/scope-detail-panel";
import ScopeItemModal from "./components/scope-item-modal";
import BulkEditDesignScopeDetails from "./components/bulk-edit-design-scope-details";
import BulkLinkDesignPackageDrawer from "./components/bulk-link-design-package-drawer";
import BulkLinkBidPackageDrawer from "./components/bulk-link-bid-package-drawer";
import BulkEditClassificationDrawer from "./components/bulk-edit-classification-drawer";
import BulkEditResponsibilityDrawer from "./components/bulk-edit-responsibility-drawer";
import ImportScopeItemsFromTemplateModal from "./components/import-scope-items-from-template-modal";
import BulkUnlinkPackagesModal from "./components/bulk-unlink-packages-modal";
import ImportScopeItemsCsvDrawer from "./components/import-scope-items-csv-drawer";
import "./components/scope-item.scss";
import {
  filterDesignScopeList,
  lookupsFromListItems,
  type DesignScopeListFilter
} from "./shared/design-scope-list-filter";
import { toModalScopeItem } from "./shared/design-scope-list-mappers";
import {
  toDesignScopeCreateInstance,
  toDesignScopeUpdateInstance,
  toBulkUnlinkPackagesUpdateInstance,
  type BulkUnlinkPackageOptions
} from "./shared/design-scope-create-mappers";
import {
  DESIGN_SCOPE_DELETE_SUCCESS_MESSAGE,
  DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE
} from "./shared/design-scope-messages";
import {
  BID_PACKAGE_COL_ID,
  DESIGN_PACKAGE_COL_ID,
  bidPackageColumnValue,
  designPackageColumnValue,
  sameStringSets,
  setFilterValuesFromModel,
  uniqueNonEmptySorted,
  withSetFilterValues
} from "./shared/design-scope-package-column-values";

// Silent skips during template seeding are the least debuggable failure this
// feature has, so both added and skipped counts always surface together.
function seedResultMessage(added: number, skipped: number): string {
  const addedPart = `${added} item${added === 1 ? "" : "s"} added`;
  return skipped > 0
    ? `${addedPart}, ${skipped} skipped (already present)`
    : addedPart;
}

const TREE_SYSTEM_LABEL: Record<CsiSystem, string> = {
  UF: "UniFormat",
  MF: "MasterFormat"
};

const EXPORT_EXCLUDED_COLUMNS = ["actions", "ag-Grid-SelectionColumn"];

/** Default button chrome; red label/icon (keeps grey border on hover/focus). */
const BULK_DANGER_CONTENT_BTN_CLASS =
  "text-[var(--ciq-red-700)] hover:!border-[var(--ciq-gray-300)] hover:!text-[var(--ciq-red-800)] focus:!border-[var(--ciq-gray-300)] focus:!text-[var(--ciq-red-800)] disabled:!text-[var(--ciq-red-200)]";

function nodeWhen(show: boolean, node: ReactNode) {
  if (!show) return null;
  return node;
}

/** "visible of total" while filters hide rows, like the other log headers. */
function ScopeCountBadge({ count, total }: { count: number; total: number }) {
  if (total <= 0) return null;
  return (
    <div className="flex shrink-0" data-testid="design-scope-count-badge">
      {getGridStatusBudge({
        title: "Design Scope",
        totalCount: total,
        filterStats:
          count === total
            ? undefined
            : { displayCount: count, filterName: undefined }
      })}
    </div>
  );
}

/** Scope items the grid still shows after column filters; tree groups don't count. */
function countVisibleScopeItems(api: GridApi | undefined): number | null {
  if (!api || api.isDestroyed?.()) return null;
  let count = 0;
  api.forEachNodeAfterFilter((node) => {
    if (!node.group && node.data) count += 1;
  });
  return count;
}

export default function DesignScopePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    projectToken,
    tokenRetrievalState,
    gqlClientForProject,
    projectDetails,
    isFeatureFlagEnabled,
    isProjectArchived
  } = useContext(ProjectContext);
  const isReadOnly = !!isProjectArchived;
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);
  const isExcelExportEnabled = isFeatureFlagEnabled("EXCEL_EXPORT");
  const isPrintViewEnabled = isFeatureFlagEnabled("PRINT_VIEW");
  const projectEntities = useProjectEntities(projectId);
  const { options: lodOptions, loading: lodOptionsLoading } =
    useLevelOfDetail(projectId);
  const {
    options: responsibilityOptions,
    loading: responsibilitiesLoading,
    catalogEnabled,
    addResponsibility
  } = useDesignScopeResponsibilities(projectId);

  const {
    items: allItems,
    loading,
    loadFailed,
    reload,
    deleteEntitiesFromList
  } = useDesignScopeList(projectToken || tokenRetrievalState?.token);

  const [view, setView] = useState<DesignScopePageView>("hierarchy");
  const [treeSystem, setTreeSystem] = useState<CsiSystem>("UF");
  const viewRef = useRef(view);
  const treeSystemRef = useRef(treeSystem);
  viewRef.current = view;
  treeSystemRef.current = treeSystem;
  const viewLayoutDirtyRef = useRef(false);
  const [hierarchyMenuOpen, setHierarchyMenuOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Checkbox multi-select for the bulk toolbar (separate from detail focus).
  const [selectedRows, setSelectedRows] = useState<DsmScopeItem[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkLinkDesignPackageOpen, setBulkLinkDesignPackageOpen] =
    useState(false);
  const [bulkLinkBidPackageOpen, setBulkLinkBidPackageOpen] = useState(false);
  const [bulkEditClassificationOpen, setBulkEditClassificationOpen] =
    useState(false);
  const [bulkEditResponsibilityOpen, setBulkEditResponsibilityOpen] =
    useState(false);
  const [bulkUnlinkOpen, setBulkUnlinkOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [notificationApi, notificationContextHolder] =
    notification.useNotification();
  const [editing, setEditing] = useState<DesignScopeListItem | undefined>(
    undefined
  );
  const [seed, setSeed] = useState<
    { classification?: Classifications } | undefined
  >(undefined);

  // True from the very first render when this project still has a template
  // waiting to seed — so the page shows a "setting up" state instead of the
  // empty state flashing before the seeded records arrive.
  const [seeding, setSeeding] = useState<boolean>(
    () => getPendingTemplateSeed(projectId) != null
  );
  // Held here so the filter-bar Expand/Collapse control can drive the tree.
  const gridRef = useRef<AgGridReact>(null);
  const filterChipRef = useRef<FilterChipComponentRef>(null);
  const lastAppliedColumnConfigRef = useRef<TSaveColumnState | null>(null);
  const conditionalFormattingRulesRef = useRef<ConditionalFormattingRule[]>([]);
  const [conditionalFormattingRules, setConditionalFormattingRules] = useState<
    ConditionalFormattingRule[]
  >([]);
  const [isFirstDataRendered, setIsFirstDataRendered] = useState(false);
  const isFirstDataRenderedRef = useRef(false);
  isFirstDataRenderedRef.current = isFirstDataRendered;
  const [text, setText] = useState("");
  const textRef = useRef(text);
  textRef.current = text;
  const { savedColumns, isColumnConfigReady, onSaveColumnState } =
    useDesignScopeColumnConfig({
      gridRef,
      getConditionalsFormatting: () => conditionalFormattingRulesRef.current,
      getQuickSearch: () => textRef.current,
      getViewLayout: () => ({
        view: viewRef.current,
        treeSystem: treeSystemRef.current
      })
    });
  const onSaveColumnStateRef = useRef(onSaveColumnState);
  onSaveColumnStateRef.current = onSaveColumnState;
  const [viewLayoutReady, setViewLayoutReady] = useState(false);
  const treeExpandToggle = useAgGridTreeExpandToggle({
    gridRef,
    enabled: view === "hierarchy",
    resetKey: treeSystem,
    initiallyAllExpanded: true
  });

  // Grid remounts on list/hierarchy toggle — pause persist until re-apply finishes.
  useEffect(() => {
    setIsFirstDataRendered(false);
  }, [view, treeSystem]);

  // Wait for default column config, then hydrate List/Hierarchy before first paint
  // of the toggle/grid so we don't flash the hardcoded default and remount.
  useEffect(() => {
    if (!isColumnConfigReady || viewLayoutReady) return;

    const config = savedColumns?.design_scope_column_config;
    if (config) {
      lastAppliedColumnConfigRef.current = config;
      const layout =
        readDesignScopeViewLayout(config) ?? DEFAULT_DESIGN_SCOPE_VIEW_LAYOUT;
      viewRef.current = layout.view;
      treeSystemRef.current = layout.treeSystem;
      setView(layout.view);
      setTreeSystem(layout.treeSystem);

      const nextSearch = config.quickSearch ?? "";
      setText(nextSearch);
      textRef.current = nextSearch;
      const nextRules = config.conditionals_formatting ?? [];
      conditionalFormattingRulesRef.current = nextRules;
      setConditionalFormattingRules(nextRules);
    }

    setViewLayoutReady(true);
  }, [isColumnConfigReady, savedColumns, viewLayoutReady]);

  const getViewLayout = useCallback(
    (): DesignScopeViewLayout => ({
      view: viewRef.current,
      treeSystem: treeSystemRef.current
    }),
    []
  );

  /** User-driven layout change: keep lastApplied meta in sync and persist after remount. */
  const commitViewLayout = useCallback((layout: DesignScopeViewLayout) => {
    const changed =
      layout.view !== viewRef.current ||
      layout.treeSystem !== treeSystemRef.current;
    viewRef.current = layout.view;
    treeSystemRef.current = layout.treeSystem;
    setView(layout.view);
    setTreeSystem(layout.treeSystem);
    if (lastAppliedColumnConfigRef.current) {
      lastAppliedColumnConfigRef.current = withDesignScopeViewMeta(
        lastAppliedColumnConfigRef.current,
        layout
      );
    }
    if (changed) viewLayoutDirtyRef.current = true;
  }, []);
  const [importFromTemplateOpen, setImportFromTemplateOpen] = useState(false);
  const [importCsvOpen, setImportCsvOpen] = useState(false);

  const [designPackageFilter, setDesignPackageFilter] = useState<string[]>([]);
  const [bidPackageFilter, setBidPackageFilter] = useState<string[]>([]);
  const designPackageFilterRef = useRef(designPackageFilter);
  const bidPackageFilterRef = useRef(bidPackageFilter);
  designPackageFilterRef.current = designPackageFilter;
  bidPackageFilterRef.current = bidPackageFilter;

  const hasFilters =
    Boolean(text) ||
    designPackageFilter.length > 0 ||
    bidPackageFilter.length > 0;

  const filter: DesignScopeListFilter = useMemo(
    () => ({
      text: text || undefined
    }),
    [text]
  );

  const items = useMemo(
    () => filterDesignScopeList(allItems, filter),
    [allItems, filter]
  );

  const listLookups = useMemo(() => lookupsFromListItems(allItems), [allItems]);
  const entities = useMemo(
    () => ({
      ...projectEntities,
      nameOfDp: (id: string) =>
        listLookups.nameOfDp[id] ?? projectEntities.nameOfDp(id),
      nameOfBp: (id: string) =>
        listLookups.nameOfBp[id] ?? projectEntities.nameOfBp(id)
    }),
    [projectEntities, listLookups]
  );

  const tableItems = useMemo(() => items.map(toModalScopeItem), [items]);

  // Search filters rows before the grid; column / package filters run inside
  // it, so the visible count is read back from the grid.
  const [gridVisibleCount, setGridVisibleCount] = useState<number | null>(null);
  const refreshVisibleCount = useCallback(() => {
    setGridVisibleCount(countVisibleScopeItems(gridRef.current?.api));
  }, []);
  const visibleCount =
    items.length === 0 ? 0 : (gridVisibleCount ?? items.length);

  // Package dropdown options = unique labels currently present in the DP/BP
  // columns (same strings the AG Grid set filters use), not the full entity lists.
  const designPackageOptions = useMemo(
    () =>
      uniqueNonEmptySorted(
        tableItems.map((item) =>
          designPackageColumnValue(item, entities.nameOfDp)
        )
      ).map((value) => ({ value, label: value })),
    [tableItems, entities]
  );
  const bidPackageOptions = useMemo(
    () =>
      uniqueNonEmptySorted(
        tableItems.map((item) => bidPackageColumnValue(item, entities.nameOfBp))
      ).map((value) => ({ value, label: value })),
    [tableItems, entities]
  );

  const applyPackageFiltersToGrid = useCallback(
    (dpValues: string[], bpValues: string[]) => {
      const api = gridRef.current?.api;
      if (!api?.setFilterModel) return;
      let model = api.getFilterModel?.() ?? {};
      model = withSetFilterValues(model, DESIGN_PACKAGE_COL_ID, dpValues);
      model = withSetFilterValues(model, BID_PACKAGE_COL_ID, bpValues);
      api.setFilterModel(model);
    },
    []
  );

  const syncPackageFiltersFromGrid = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api?.getFilterModel) return;
    const model = api.getFilterModel() ?? {};
    const nextDp = setFilterValuesFromModel(model, DESIGN_PACKAGE_COL_ID);
    const nextBp = setFilterValuesFromModel(model, BID_PACKAGE_COL_ID);
    setDesignPackageFilter((prev) =>
      sameStringSets(prev, nextDp) ? prev : nextDp
    );
    setBidPackageFilter((prev) =>
      sameStringSets(prev, nextBp) ? prev : nextBp
    );
  }, []);

  const clearFilters = () => {
    setText("");
    textRef.current = "";
    gridRef.current?.api?.setGridOption("quickFilterText", "");
    if (lastAppliedColumnConfigRef.current) {
      lastAppliedColumnConfigRef.current = {
        ...lastAppliedColumnConfigRef.current,
        quickSearch: ""
      };
    }
    setDesignPackageFilter([]);
    setBidPackageFilter([]);
    applyPackageFiltersToGrid([], []);
  };

  const onDesignPackageFilterChange = (values: string[]) => {
    setDesignPackageFilter(values);
    applyPackageFiltersToGrid(values, bidPackageFilterRef.current);
  };

  const onBidPackageFilterChange = (values: string[]) => {
    setBidPackageFilter(values);
    applyPackageFiltersToGrid(designPackageFilterRef.current, values);
  };
  // Seed from a template chosen at project creation. It couldn't run then (no
  // project token yet), so it was deferred to the first visit here, where the
  // project-scoped token exists. Runs once; the intent is cleared only on
  // success so a failed seed retries on the next visit.
  const seedAttemptedRef = useRef<string | null>(null);
  useEffect(() => {
    const templateId = getPendingTemplateSeed(projectId);
    if (!templateId || isProjectArchived) {
      setSeeding(false);
      return;
    }
    if (seedAttemptedRef.current === projectId) return;
    seedAttemptedRef.current = projectId;
    setSeeding(true);
    (async () => {
      try {
        const { added, skipped } =
          await dsmTemplatesApi.seedProjectFromTemplate(projectId, templateId);
        clearPendingTemplateSeed(projectId);
        if (added > 0) {
          message.success(
            `Design scope ready — ${seedResultMessage(added, skipped)}.`
          );
          await reload({ silent: true });
        } else if (skipped > 0) {
          message.info(
            `Design scope already up to date — ${seedResultMessage(added, skipped)}.`
          );
        }
      } catch {
        // leave the pending intent in place so the next visit retries
        seedAttemptedRef.current = null;
        message.error(
          "We couldn't set up the template automatically. It will retry the next time you open this page."
        );
      } finally {
        setSeeding(false);
      }
    })();
  }, [projectId, reload, isProjectArchived]);

  const selected = allItems.find((i) => i.id === selectedId) ?? null;
  const selectedPoc = selected ? toModalScopeItem(selected) : null;

  const openNew = () => {
    setEditing(undefined);
    setSeed(undefined);
    setModalOpen(true);
  };
  const openEdit = (item: DsmScopeItem) => {
    setEditing(allItems.find((row) => row.id === item.id));
    setSeed(undefined);
    setModalOpen(true);
  };
  const addUnder = useCallback((c: Classification | null) => {
    setEditing(undefined);
    // The tree hands back one segment; the modal seeds the slot it belongs to.
    setSeed(c ? { classification: foldToPair([c]) } : undefined);
    setModalOpen(true);
  }, []);

  const submit = async (input: ScopeItemInput) => {
    const token = projectToken || tokenRetrievalState?.token;
    if (!token) {
      message.error("Project auth is not ready. Please try again.");
      return;
    }

    if (editing) {
      const updatePayload = {
        feature_type: "DESIGN_SCOPE" as const,
        feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
        feature_instances: [toDesignScopeUpdateInstance(input, editing.id)]
      };

      const response = await updateDesignScopeInstances(updatePayload, token);
      if (!response.success) {
        message.error(response.errorMsg || "Unable to update scope item.");
        return;
      }

      setModalOpen(false);
      setEditing(undefined);
      message.success(DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE);
      await reload({ silent: true });
      return;
    }

    const createPayload = {
      feature_type: "DESIGN_SCOPE" as const,
      feature_instances: [toDesignScopeCreateInstance(input)]
    };

    const response = await createDesignScopeInstances(createPayload, token);

    if (!response.success) {
      message.error(response.errorMsg || "Unable to create scope item.");
      return;
    }

    setModalOpen(false);
    setEditing(undefined);
    notificationApi.open({
      message: (
        <div className="notifications space-x-1">
          <span className="notification-highlight-title">{input.title}</span>
          <span className="notification-title">successfully created.</span>
        </div>
      ),
      placement: "top"
    });
    await reload({ silent: true });
  };

  const applyColumnConfig = useCallback((columnStates: TSaveColumnState) => {
    lastAppliedColumnConfigRef.current = columnStates;

    // Scope search is React-side (`text` → filterDesignScopeList). Do not push
    // `quickSearch` into AG Grid quickFilterText — that would keep filtering
    // after the search box is cleared.
    setText(columnStates?.quickSearch ?? "");
    textRef.current = columnStates?.quickSearch ?? "";
    const nextRules = columnStates?.conditionals_formatting ?? [];
    conditionalFormattingRulesRef.current = nextRules;
    setConditionalFormattingRules(nextRules);

    const layout = readDesignScopeViewLayout(columnStates);
    if (layout) {
      const layoutChanged =
        layout.view !== viewRef.current ||
        layout.treeSystem !== treeSystemRef.current;
      if (layoutChanged) {
        viewRef.current = layout.view;
        treeSystemRef.current = layout.treeSystem;
        setView(layout.view);
        setTreeSystem(layout.treeSystem);
        // Grid remounts; onFirstDataRendered re-enters with matching layout.
        return;
      }
    }

    if (!gridRef.current?.api || gridRef.current.api.isDestroyed?.()) return;
    const layoutForColumns = readDesignScopeViewLayout(columnStates) ?? {
      view: viewRef.current,
      treeSystem: treeSystemRef.current
    };
    UtilColumnConfig.setFilterStateFromData({
      columnStates: {
        ...columnStates,
        quickSearch: "",
        columnList: reconcileDesignScopeColumnList(
          layoutForColumns,
          columnStates.columnList ?? []
        )
      },
      gridRef: {
        api: gridRef.current.api
      }
    });
    // Saved / default views with no sortOrder still show a present ID sort,
    // matching Submittal/Material when their config carries ID sort (or Submittal's
    // empty-config fallback). Do not override a view that already sorted a column.
    ensureDesignScopeDefaultSort(gridRef.current.api, columnStates);
  }, []);

  const getColumnStateFromGrid = useCallback((): TSaveColumnState | null => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed?.()) return null;
    return withDesignScopeViewMeta(
      {
        ...UtilColumnConfig.getColumnStateFromGrid({
          gridRef: {
            api
          },
          isIncludeSearchText: false,
          conditionals_formatting: conditionalFormattingRulesRef.current
        }),
        quickSearch: textRef.current
      },
      getViewLayout()
    );
  }, [getViewLayout]);

  const persistColumnStateIfReady = useCallback(() => {
    if (!isFirstDataRenderedRef.current) return;
    const state = getColumnStateFromGrid();
    if (state) lastAppliedColumnConfigRef.current = state;
    onSaveColumnStateRef.current();
  }, [getColumnStateFromGrid]);

  // After a user view-layout change remounts the grid, persist once ready.
  useEffect(() => {
    if (!isFirstDataRendered || !viewLayoutDirtyRef.current) return;
    viewLayoutDirtyRef.current = false;
    persistColumnStateIfReady();
  }, [isFirstDataRendered, persistColumnStateIfReady]);

  const onSearchTextChange = useCallback(
    (next: string) => {
      setText(next);
      textRef.current = next;
      gridRef.current?.api?.setGridOption("quickFilterText", "");
      if (lastAppliedColumnConfigRef.current) {
        lastAppliedColumnConfigRef.current = {
          ...lastAppliedColumnConfigRef.current,
          quickSearch: next
        };
      }
      persistColumnStateIfReady();
    },
    [persistColumnStateIfReady]
  );

  const onConditionalFormattingRulesChange = useCallback(
    (rules: ConditionalFormattingRule[]) => {
      conditionalFormattingRulesRef.current = rules;
      setConditionalFormattingRules(rules);
      persistColumnStateIfReady();
    },
    [persistColumnStateIfReady]
  );

  const onGridFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      filterChipRef.current?.onFilterApplied();
      // Same rule as Material / Bid Package logs: a set filter clears the
      // selection so hidden rows never stay selected behind the filter.
      updateGridSelectionOnFilter(event, gridRef);
      syncPackageFiltersFromGrid();
      persistColumnStateIfReady();
    },
    [syncPackageFiltersFromGrid, persistColumnStateIfReady]
  );

  const onGridSortChanged = useCallback(() => {
    persistColumnStateIfReady();
  }, [persistColumnStateIfReady]);

  const onGridColumnVisible = useCallback(
    (e: { api?: unknown; source?: string }) => {
      if (e.api && e.source === "toolPanelUi") {
        persistColumnStateIfReady();
      }
    },
    [persistColumnStateIfReady]
  );

  const onGridColumnResized = useCallback(
    (e: { finished?: boolean; source?: string }) => {
      // Drag-stop ignores the resize handle. Persist only a finished user resize,
      // matching Material log, so applying a saved layout does not write itself back.
      if (e.finished && e.source === "uiColumnResized") {
        persistColumnStateIfReady();
      }
    },
    [persistColumnStateIfReady]
  );

  const onGridDragStopped = useCallback(
    (e: { api?: unknown; target?: { className?: string } }) => {
      const classNames = [
        "ag-header-cell-resize",
        "ag-tool-panel-horizontal-resize"
      ];
      if (e.api && !classNames.includes(e.target?.className ?? "")) {
        persistColumnStateIfReady();
      }
    },
    [persistColumnStateIfReady]
  );

  const onGridFirstDataRendered = useCallback(() => {
    if (lastAppliedColumnConfigRef.current) {
      applyColumnConfig(lastAppliedColumnConfigRef.current);
      syncPackageFiltersFromGrid();
    } else if (savedColumns?.design_scope_column_config) {
      applyColumnConfig(savedColumns.design_scope_column_config);
      syncPackageFiltersFromGrid();
    } else {
      // Remount (list/hierarchy swap) with no saved layout — keep toolbar filters.
      applyPackageFiltersToGrid(
        designPackageFilterRef.current,
        bidPackageFilterRef.current
      );
      ensureDesignScopeDefaultSort(gridRef.current?.api, null);
    }

    setTimeout(() => {
      setIsFirstDataRendered(true);
    }, 500);
  }, [
    applyColumnConfig,
    applyPackageFiltersToGrid,
    savedColumns,
    syncPackageFiltersFromGrid
  ]);

  const renderList = () => {
    if (seeding)
      return (
        <div
          className="flex h-full flex-col items-center justify-center gap-4 py-16 text-center"
          data-testid="scope-seeding"
        >
          <Spin size="large" />
          <Space orientation="vertical" size={2}>
            <Typography.Text strong>
              Setting up your Design Scope…
            </Typography.Text>
            <Typography.Text type="secondary">
              Adding scope items from the template you selected. This only
              happens once — it&apos;ll be ready in a moment.
            </Typography.Text>
          </Space>
        </div>
      );
    if (!viewLayoutReady || loading)
      return (
        <div
          className="flex justify-center py-16"
          data-testid="scope-layout-loading"
        >
          <Spin />
        </div>
      );
    // API failure already surfaces via toast — do not show the empty-project CTA.
    if (loadFailed && allItems.length === 0) return null;
    // The grid stays mounted when empty so its column headers remain visible;
    // the blank / no-results state renders as the grid's no-rows overlay.
    const noRowsOverlay =
      allItems.length === 0 ? (
        <BlankState
          title="No scope items added"
          description="Create or Import Scope Items list to start."
          // Design order: Add New · Import CSV (highlighted) · Import List.
          secondaryActions={[
            {
              label: "Add New",
              onClick: openNew,
              disabled: isReadOnly,
              "data-testid": "scope-blank-add-new"
            },
            {
              label: "Import CSV",
              type: "primary",
              onClick: () => setImportCsvOpen(true),
              disabled: isReadOnly,
              "data-testid": "scope-blank-import-csv"
            },
            {
              label: "Import List",
              onClick: () => setImportFromTemplateOpen(true),
              disabled: isReadOnly,
              "data-testid": "scope-blank-import-list"
            }
          ]}
          data-testid="scope-blank-state"
        />
      ) : (
        <BlankState
          title="No scope items match your search or filters"
          secondaryActions={
            hasFilters
              ? [
                  {
                    label: "Clear filters",
                    onClick: clearFilters,
                    "data-testid": "scope-clear-filters"
                  }
                ]
              : []
          }
          data-testid="scope-no-results"
        />
      );
    // One grid for both views: hierarchy mode groups the same rows by
    // classification, so no column information is lost on toggle.
    return (
      <ScopeTable
        items={tableItems}
        entities={entities}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onEdit={isReadOnly ? undefined : openEdit}
        hierarchy={view === "hierarchy"}
        treeSystem={treeSystem}
        onAddUnder={isReadOnly ? undefined : addUnder}
        gridRef={gridRef}
        treeExpandSync={
          view === "hierarchy" ? treeExpandToggle.gridHandlers : undefined
        }
        onFirstDataRendered={onGridFirstDataRendered}
        noRowsOverlay={noRowsOverlay}
        onFilterChanged={onGridFilterChanged}
        onModelUpdated={refreshVisibleCount}
        onSortChanged={onGridSortChanged}
        onColumnVisible={onGridColumnVisible}
        onColumnResized={onGridColumnResized}
        onDragStopped={onGridDragStopped}
        conditionalFormattingRules={conditionalFormattingRules}
        onConditionalFormattingRulesChange={onConditionalFormattingRulesChange}
        onSelectionChanged={() => {
          const rows = (gridRef.current?.api?.getSelectedRows() ?? []).filter(
            (row): row is DsmScopeItem => Boolean(row?.id)
          );
          setSelectedRows(rows);
        }}
      />
    );
  };

  const clearRowSelection = useCallback(() => {
    setSelectedRows([]);
    gridRef.current?.api?.deselectAll();
  }, []);

  const openBulkUnlink = useCallback(() => {
    if (selectedRows.length === 0) return;
    setBulkUnlinkOpen(true);
  }, [selectedRows.length]);

  const performBulkUnlink = useCallback(
    async (options: BulkUnlinkPackageOptions) => {
      if (selectedRows.length === 0) return;
      const token = projectToken || tokenRetrievalState?.token;
      if (!token) {
        message.error("Project auth is not ready. Please try again.");
        return;
      }
      const response = await updateDesignScopeInstances(
        {
          feature_type: "DESIGN_SCOPE",
          feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
          feature_instances: selectedRows.map((row) =>
            toBulkUnlinkPackagesUpdateInstance(
              row.id,
              options,
              row.commitments,
              row.bids
            )
          )
        },
        token
      );
      if (!response.success) {
        message.error(
          response.errorMsg || "Unable to unlink packages from scope items."
        );
        return;
      }
      message.success(DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE);
      setBulkUnlinkOpen(false);
      clearRowSelection();
      await reload({ silent: true });
    },
    [
      selectedRows,
      projectToken,
      tokenRetrievalState?.token,
      clearRowSelection,
      reload
    ]
  );

  const deleteScopeItems = useCallback(
    async (ids: string[]) => {
      if (!ids.length) return false;
      const token = projectToken || tokenRetrievalState?.token;
      if (!token) {
        message.error("Project auth is not ready. Please try again.");
        return false;
      }
      const response = await deleteDesignScopeInstances(
        {
          feature_instance_ids: ids,
          feature_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID
        },
        token
      );
      if (!response.success) {
        message.error(response.errorMsg || "Unable to delete scope item.");
        return false;
      }
      message.success(DESIGN_SCOPE_DELETE_SUCCESS_MESSAGE);
      if (selectedId && ids.includes(selectedId)) {
        setSelectedId(null);
      }
      clearRowSelection();
      // Local removal — avoid full reload overlay; change events will no-op if
      // the same ids arrive again.
      deleteEntitiesFromList(ids);
      return true;
    },
    [
      projectToken,
      tokenRetrievalState?.token,
      selectedId,
      clearRowSelection,
      deleteEntitiesFromList
    ]
  );

  const confirmBulkDelete = useCallback(() => {
    if (selectedRows.length === 0) return;
    Modal.confirm({
      title: "Are you sure you want to delete the selected scope item(s)?",
      content: "This can't be undone.",
      okText: "Delete",
      okButtonProps: { danger: true, className: "ciq-delete-btn" },
      cancelText: "Cancel",
      onOk: async () => {
        await deleteScopeItems(selectedRows.map((row) => row.id));
      }
    });
  }, [selectedRows, deleteScopeItems]);

  const deleteFromDrawer = useCallback(async () => {
    if (!editing?.id) return;
    const deleted = await deleteScopeItems([editing.id]);
    if (!deleted) return;
    setModalOpen(false);
    setEditing(undefined);
    setSeed(undefined);
  }, [editing?.id, deleteScopeItems]);

  const hierarchyViewMenu: MenuProps = useMemo(
    () => ({
      selectable: true,
      selectedKeys: view === "hierarchy" ? [treeSystem] : [],
      items: [
        {
          key: "UF",
          label: "UniFormat",
          onClick: () => {
            commitViewLayout({ view: "hierarchy", treeSystem: "UF" });
            setHierarchyMenuOpen(false);
          }
        },
        {
          key: "MF",
          label: "MasterFormat",
          onClick: () => {
            commitViewLayout({ view: "hierarchy", treeSystem: "MF" });
            setHierarchyMenuOpen(false);
          }
        }
      ]
    }),
    [view, treeSystem, commitViewLayout]
  );

  const hierarchySegmentLabel =
    view === "hierarchy" ? TREE_SYSTEM_LABEL[treeSystem] : "Hierarchy View";

  const addScopeItemMenu: MenuProps = {
    className: "w-[330px]",
    items: [
      {
        key: "create_new",
        onClick: () => openNew(),
        className: "px-4 py-2",
        label: (
          <div className="flex flex-col space-y-0.5">
            <div className="font-medium text-black text-opacity-90">
              Add Individually
            </div>
            <div className="text-ciq-gray-900 text-xs">
              Add a single Scope Item manually
            </div>
          </div>
        )
      },
      {
        key: "import_csv",
        className: "px-4 py-2",
        onClick: () => setImportCsvOpen(true),
        label: (
          <div
            className="flex flex-col space-y-0.5"
            data-testid="scope-import-csv-menu-item"
          >
            <div className="font-medium text-black text-opacity-90">
              Import a CSV
            </div>
            <div className="text-ciq-gray-900 text-xs">
              Upload a CSV to add or update Scope Items
            </div>
          </div>
        )
      },
      {
        key: "import_log",
        className: "px-4 py-2",
        onClick: () => setImportFromTemplateOpen(true),
        label: (
          <div
            className="flex flex-col space-y-0.5"
            data-testid="scope-import-menu-item"
          >
            <div className="font-medium text-black text-opacity-90">
              Import from List
            </div>
            <div className="text-ciq-gray-900 text-xs">
              Add Scope Items from one or more pre-seeded lists
            </div>
          </div>
        )
      }
    ]
  };

  const onExportCsv = () => {
    if (!projectDetails || !gridRef.current) return;
    const filename = `Design_Scope_Log_${projectDetails.name}.csv`;
    exportEntityLogAsCSV({
      filename,
      gridRef: {
        api: gridRef.current.api
      },
      includedColumns: [],
      excludedColumns: EXPORT_EXCLUDED_COLUMNS,
      appendDesignPackageRows: true
    });
    saveAuditLogEvent({
      eventType: designScopeExportEventType("CSV", "scope", treeSystem),
      featureType: "DESIGN_SCOPE",
      filename
    });
  };

  const onExportExcel = (options: ExcelExportOptions) => {
    if (!gridRef.current || !projectDetails) return;
    const filename = `Design_Scope_Log_${sanitizeSheetName(
      projectDetails.name
    )}.xlsx`;
    exportEntityLogAsExcel({
      projectDetails,
      gridRef,
      filename,
      prependText: `${
        projectDetails.name
      } - Design Scope Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
        `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
      )}`,
      excludedColumns: EXPORT_EXCLUDED_COLUMNS,
      conditionalsFormatting: conditionalFormattingRulesRef.current,
      appendDesignPackageRows: true,
      options
    });
    saveAuditLogEvent({
      eventType: designScopeExportEventType("EXCEL", view, treeSystem),
      featureType: "DESIGN_SCOPE",
      filename
    });
  };

  const onOpenPrintView = () => {
    if (!gridRef.current || !projectDetails) {
      message.error("Unable to open print view. Grid data is not available.");
      return;
    }

    try {
      sessionStorage.clear();
      const printViewState: DesignScopeLogPrintViewState = {
        pageId: "design-scope-log",
        title: `${
          projectDetails.name || "Project"
        } - Design Scope Log - Exported from ConstructivIQ on ${DateUtils.dateTimeObj().format(
          `M/D [at] h:mma [${DateUtils.dateTimeObj().format("z")}]`
        )}`,
        initialState: gridRef.current.api.getState(),
        context: {},
        skipColumnIds: EXPORT_EXCLUDED_COLUMNS,
        headerHeight: gridRef.current.props?.headerHeight,
        // Package columns stack every linked name. A fixed height would clip them.
        rowHeight: undefined,
        conditionalsFormatting: conditionalFormattingRulesRef.current,
        hierarchy: view === "hierarchy",
        treeSystem,
        pdfFileBaseName: designScopePdfFileBaseName(projectDetails.name)
      };
      sessionStorage.setItem(
        PRINT_VIEW_STORAGE_KEY,
        JSON.stringify(printViewState)
      );
      window.open(`/project/${projectId}/printView`, "_blank");
    } catch (error) {
      console.error("Error opening design scope log print view:", error);
      message.error("An error occurred while opening print view.");
    }
  };

  const openPrintValidation = () => {
    if (!gridRef.current) return;
    PrintViewValidationModal({
      gridApi: gridRef.current.api,
      onProceed: onOpenPrintView,
      skipColumnIds: EXPORT_EXCLUDED_COLUMNS
    });
  };

  const exportOptions: ExportFormatOption[] = [];
  if (view === "scope") {
    exportOptions.push({
      key: "export_csv",
      title: "CSV",
      testId: "scope-export-csv"
    });
  }
  if (isExcelExportEnabled) {
    exportOptions.push({
      key: "export_excel",
      title: "Excel (.xlsx)",
      isExcel: true,
      testId: "scope-export-excel"
    });
  }
  if (isPrintViewEnabled) {
    exportOptions.push({
      key: "print_view",
      title: "PDF/Print",
      testId: "scope-export-pdf"
    });
  }

  const onProceedExport = ({ key, excelOptions }: ExportSelection) => {
    if (key === "export_excel") {
      if (excelOptions) onExportExcel(excelOptions);
      return;
    }
    if (key === "print_view") {
      openPrintValidation();
      return;
    }
    onExportCsv();
  };

  const exportControl =
    exportOptions.length > 0 ? (
      <ExportOptionsPopover
        options={exportOptions}
        onProceed={onProceedExport}
        excelTableName={projectDetails?.name || "Project"}
        showIncludeComments={false}
        disabled={!!isProjectArchived}
        buttonType="default"
        buttonTestId="scope-export-btn"
      />
    ) : null;

  function designScopePageBody() {
    return (
      <div
        className="dsm-v2 flex h-full flex-col"
        data-testid="design-scope-page"
      >
        {notificationContextHolder}
        <div className="flex flex-col gap-3 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CiqIconBox feature="DESIGN_SCOPE" size={20} padding={8} />
              <div>
                <div className="flex items-center gap-2">
                  <Typography.Title level={4} style={{ marginBottom: 0 }}>
                    Design Scope
                  </Typography.Title>
                  <Tag data-testid="design-scope-beta-tag">Beta</Tag>
                  <ScopeCountBadge
                    count={visibleCount}
                    total={allItems.length}
                  />
                </div>
              </div>
            </div>
            <Space wrap>
              {nodeWhen(
                viewLayoutReady,
                <Segmented
                  data-testid="scope-view-toggle"
                  value={view}
                  onChange={(v) => {
                    if (v === "scope") {
                      commitViewLayout({
                        view: "scope",
                        treeSystem: treeSystemRef.current
                      });
                      setHierarchyMenuOpen(false);
                      return;
                    }
                    // Stay on current view until a menu item is chosen; just open
                    // the UniFormat / MasterFormat picker.
                    setHierarchyMenuOpen(true);
                  }}
                  options={[
                    {
                      value: "scope",
                      label: (
                        <span data-testid="scope-list-view">List View</span>
                      )
                    },
                    {
                      value: "hierarchy",
                      label: (
                        <Dropdown
                          open={hierarchyMenuOpen}
                          onOpenChange={setHierarchyMenuOpen}
                          menu={hierarchyViewMenu}
                          trigger={["click"]}
                        >
                          <span
                            role="button"
                            tabIndex={0}
                            className="inline-flex items-center gap-1"
                            data-testid="scope-hierarchy-view"
                            onClick={() => setHierarchyMenuOpen(true)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setHierarchyMenuOpen(true);
                              }
                            }}
                          >
                            {hierarchySegmentLabel}
                            <DownOutlined
                              className="text-xs"
                              data-testid="scope-hierarchy-menu-trigger"
                            />
                          </span>
                        </Dropdown>
                      )
                    }
                  ]}
                />
              )}
              <FilterChipComponent
                key="DesignScopeLog_FilterChip"
                gridRef={gridRef}
                ref={filterChipRef}
                data-testid="scope-filter-chip"
              />
              <div data-testid="scope-views">
                <ColumnConfigViews
                  key="DesignScopeLog_ColumnConfigViews"
                  featureId={EFeaturePageId.DESIGN_SCOPE}
                  setFilterStateFromData={applyColumnConfig}
                  getColumnStateFromGrid={getColumnStateFromGrid}
                />
              </div>
              {exportControl}
              <Dropdown
                menu={addScopeItemMenu}
                trigger={["click", "hover"]}
                disabled={isReadOnly}
              >
                <Button
                  type="primary"
                  data-testid="scope-new"
                  disabled={isReadOnly}
                >
                  Add New <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </div>

          <BulkSelectionActionBar
            selectedCount={selectedRows.length}
            onClear={clearRowSelection}
            data-testid="scope-bulk-action-bar"
          >
            <Button
              type="primary"
              icon={<LinkOutlined />}
              data-testid="scope-bulk-link-design-package"
              disabled={isReadOnly}
              onClick={() => setBulkLinkDesignPackageOpen(true)}
            >
              Link to Design Package
            </Button>
            <Button
              type="primary"
              icon={<LinkOutlined />}
              data-testid="scope-bulk-link-bid-package"
              disabled={isReadOnly}
              onClick={() => setBulkLinkBidPackageOpen(true)}
            >
              Link to Bid Package
            </Button>
            <Button
              icon={<FormOutlined />}
              data-testid="scope-bulk-edit-classification"
              disabled={isReadOnly}
              onClick={() => setBulkEditClassificationOpen(true)}
            >
              Edit Classification
            </Button>
            <Button
              icon={<UserOutlined />}
              data-testid="scope-bulk-responsibility"
              disabled={isReadOnly}
              onClick={() => setBulkEditResponsibilityOpen(true)}
            >
              Responsibility
            </Button>
            <Button
              icon={<DisconnectOutlined style={{ color: "inherit" }} />}
              className={BULK_DANGER_CONTENT_BTN_CLASS}
              data-testid="scope-bulk-unlink"
              disabled={isReadOnly}
              onClick={openBulkUnlink}
            >
              Unlink
            </Button>
            <Button
              icon={<DeleteOutlined style={{ color: "inherit" }} />}
              className={BULK_DANGER_CONTENT_BTN_CLASS}
              data-testid="scope-bulk-delete"
              disabled={isReadOnly}
              onClick={confirmBulkDelete}
            >
              Delete
            </Button>
          </BulkSelectionActionBar>

          <div className="flex flex-wrap items-end justify-between gap-2">
            <Space wrap>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Search scope items…"
                data-testid="scope-search"
                style={{ width: 240 }}
                value={text}
                onChange={(e) => onSearchTextChange(e.target.value)}
              />
              <div
                data-testid="scope-dp-filter"
                data-option-values={designPackageOptions
                  .map((o) => o.value)
                  .join("|")}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="Design Package"
                  aria-label="Design Package"
                  maxTagCount="responsive"
                  style={{ width: 200 }}
                  options={designPackageOptions}
                  value={designPackageFilter}
                  onChange={onDesignPackageFilterChange}
                  optionFilterProp="label"
                />
                {/* jsdom + Ant Select portals are flaky; tests drive onChange here. */}
                <button
                  type="button"
                  className="hidden"
                  tabIndex={-1}
                  data-testid="scope-dp-filter-select-first"
                  onClick={() => {
                    const first = designPackageOptions[0]?.value;
                    if (first) onDesignPackageFilterChange([first]);
                  }}
                />
              </div>
              <div
                data-testid="scope-bp-filter"
                data-option-values={bidPackageOptions
                  .map((o) => o.value)
                  .join("|")}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  placeholder="Bid Package"
                  aria-label="Bid Package"
                  maxTagCount="responsive"
                  style={{ width: 200 }}
                  options={bidPackageOptions}
                  value={bidPackageFilter}
                  onChange={onBidPackageFilterChange}
                  optionFilterProp="label"
                />
              </div>{" "}
              {hasFilters && (
                <Button type="link" onClick={clearFilters}>
                  Clear
                </Button>
              )}
            </Space>
            <Space>
              {seeding && (
                <Typography.Text type="secondary">Setting up…</Typography.Text>
              )}
              {view === "hierarchy" && (
                <Button
                  type="link"
                  className="!text-ciq-primary-950 relative top-[8px] px-0"
                  icon={
                    treeExpandToggle.showCollapseAll ? (
                      <ShrinkOutlined />
                    ) : (
                      <ArrowsAltOutlined />
                    )
                  }
                  data-testid="hierarchy-tree-toggle"
                  onClick={treeExpandToggle.toggleExpandCollapse}
                >
                  {treeExpandToggle.showCollapseAll
                    ? "Collapse All"
                    : "Expand All"}
                </Button>
              )}
            </Space>
          </div>
        </div>

        {/* Table + detail: grid is full width until a row is selected. */}
        <div
          className={`grid min-h-0 flex-1 gap-1.5 overflow-hidden bg-white ${
            selectedPoc ? "grid-cols-[1fr_400px]" : "grid-cols-1"
          }`}
        >
          <Card
            className="dsm-scope-surface min-h-0 overflow-auto"
            styles={{ body: { padding: 0, height: "100%" } }}
          >
            <ErrorBoundary>{renderList()}</ErrorBoundary>
          </Card>
          {selectedPoc && (
            <Card
              className="dsm-scope-surface min-h-0 overflow-hidden"
              styles={{ body: { padding: 0, height: "100%" } }}
            >
              <ErrorBoundary>
                <ScopeDetailPanel
                  item={selectedPoc}
                  projectId={projectId!}
                  entities={entities}
                  onEdit={isReadOnly ? undefined : openEdit}
                  onClose={() => setSelectedId(null)}
                />
              </ErrorBoundary>
            </Card>
          )}
        </div>

        <ErrorBoundary
          onError={() => {
            setModalOpen(false);
            setEditing(undefined);
            setSeed(undefined);
          }}
        >
          <ScopeItemModal
            open={modalOpen}
            initial={editing ? toModalScopeItem(editing) : undefined}
            seed={seed}
            entities={entities}
            lodOptions={lodOptions}
            lodOptionsLoading={lodOptionsLoading}
            responsibilities={
              catalogEnabled ? responsibilityOptions : undefined
            }
            responsibilitiesLoading={
              catalogEnabled ? responsibilitiesLoading : undefined
            }
            onAddResponsibility={catalogEnabled ? addResponsibility : undefined}
            existingTitles={allItems.map((item) => ({
              id: item.id,
              title: item.title
            }))}
            onSubmit={submit}
            onDelete={deleteFromDrawer}
            onClose={() => {
              setModalOpen(false);
              setEditing(undefined);
              setSeed(undefined);
            }}
          />
        </ErrorBoundary>

        <ErrorBoundary onError={() => setBulkEditOpen(false)}>
          <BulkEditDesignScopeDetails
            open={bulkEditOpen}
            selectedRows={selectedRows}
            entities={entities}
            lodOptions={lodOptions}
            lodOptionsLoading={lodOptionsLoading}
            responsibilities={
              catalogEnabled ? responsibilityOptions : undefined
            }
            responsibilitiesLoading={
              catalogEnabled ? responsibilitiesLoading : undefined
            }
            onAddResponsibility={catalogEnabled ? addResponsibility : undefined}
            projectToken={projectToken || tokenRetrievalState?.token || ""}
            onClose={() => setBulkEditOpen(false)}
            onUpdateDone={async () => {
              setBulkEditOpen(false);
              setSelectedRows([]);
              gridRef.current?.api?.deselectAll();
              await reload({ silent: true });
            }}
          />
        </ErrorBoundary>

        <ErrorBoundary onError={() => setBulkLinkDesignPackageOpen(false)}>
          <BulkLinkDesignPackageDrawer
            open={bulkLinkDesignPackageOpen}
            selectedRows={selectedRows}
            entities={entities}
            lodOptions={lodOptions}
            lodOptionsLoading={lodOptionsLoading}
            projectToken={projectToken || tokenRetrievalState?.token || ""}
            onClose={() => setBulkLinkDesignPackageOpen(false)}
            onLinked={async () => {
              setBulkLinkDesignPackageOpen(false);
              clearRowSelection();
              await reload({ silent: true });
            }}
          />
        </ErrorBoundary>

        <ErrorBoundary onError={() => setBulkLinkBidPackageOpen(false)}>
          <BulkLinkBidPackageDrawer
            open={bulkLinkBidPackageOpen}
            selectedRows={selectedRows}
            entities={entities}
            projectToken={projectToken || tokenRetrievalState?.token || ""}
            onClose={() => setBulkLinkBidPackageOpen(false)}
            onLinked={async () => {
              setBulkLinkBidPackageOpen(false);
              clearRowSelection();
              await reload({ silent: true });
            }}
          />
        </ErrorBoundary>

        <ErrorBoundary onError={() => setBulkEditResponsibilityOpen(false)}>
          <BulkEditResponsibilityDrawer
            open={bulkEditResponsibilityOpen}
            selectedRows={selectedRows}
            projectToken={projectToken || tokenRetrievalState?.token || ""}
            responsibilities={
              catalogEnabled ? responsibilityOptions : undefined
            }
            responsibilitiesLoading={
              catalogEnabled ? responsibilitiesLoading : undefined
            }
            onAddResponsibility={catalogEnabled ? addResponsibility : undefined}
            onClose={() => setBulkEditResponsibilityOpen(false)}
            onSaved={async () => {
              setBulkEditResponsibilityOpen(false);
              clearRowSelection();
              await reload({ silent: true });
            }}
          />
        </ErrorBoundary>

        <ErrorBoundary onError={() => setBulkEditClassificationOpen(false)}>
          <BulkEditClassificationDrawer
            open={bulkEditClassificationOpen}
            selectedRows={selectedRows}
            projectToken={projectToken || tokenRetrievalState?.token || ""}
            onClose={() => setBulkEditClassificationOpen(false)}
            onSaved={async () => {
              setBulkEditClassificationOpen(false);
              clearRowSelection();
              await reload({ silent: true });
            }}
          />
        </ErrorBoundary>

        <ImportScopeItemsFromTemplateModal
          open={importFromTemplateOpen}
          gqlClient={gqlClientForProject}
          onClose={() => setImportFromTemplateOpen(false)}
          onImported={async () => {
            await reload({ silent: true });
          }}
        />

        <BulkUnlinkPackagesModal
          open={bulkUnlinkOpen}
          onCancel={() => setBulkUnlinkOpen(false)}
          onConfirm={performBulkUnlink}
        />

        <ImportScopeItemsCsvDrawer
          open={importCsvOpen}
          projectToken={projectToken || tokenRetrievalState?.token || ""}
          gqlClient={gqlClientForProject}
          onClose={() => setImportCsvOpen(false)}
          onImported={async () => {
            await reload({ silent: true });
          }}
        />
      </div>
    );
  }
  return designScopePageBody();
}
